// @ts-nocheck
import { UserData, Conversation, Community, Post } from '../types';

export interface InteractionWeights {
    dmWeight: number;
    followingWeight: number;
    followersWeight: number;
    postInteractionWeight: number;
    communityWeight: number;
}

export const DEFAULT_WEIGHTS: InteractionWeights = {
    dmWeight: 0.4,
    followingWeight: 0.15,
    followersWeight: 0.15,
    postInteractionWeight: 0.1,
    communityWeight: 0.1,
};

/**
 * Calculates a weighted interaction score between two users.
 */
export function calculateInteractionScore(
    currentUserId: string,
    targetUserId: string,
    usersData: Record<string, UserData>,
    conversations: Conversation[],
    communities: Community[],
    posts: Post[],
    weights: InteractionWeights = DEFAULT_WEIGHTS,
    currentUserInteractions?: Set<number>,
    targetUserInteractions?: Set<number>
): number {
    let score = 0;

    const currentUser = usersData[currentUserId];
    const targetUser = usersData[targetUserId];

    if (!currentUser || !targetUser || currentUserId === targetUserId) return 0;

    // 1. Most DMs (40%) - Total messages exchanged
    const relevantConvos = (conversations || []).filter(c => 
        c?.participants && c.participants.includes(currentUserId) && c.participants.includes(targetUserId)
    );
    let totalMessages = 0;
    relevantConvos.forEach(c => {
        totalMessages += c?.messages ? c.messages.length : 0;
    });
    score += totalMessages * weights.dmWeight;

    // 2. Same Following (15%) - Mutual following
    if (currentUser.following && targetUser.following) {
        const mutualFollowing = currentUser.following.filter(uid => targetUser.following.includes(uid));
        score += mutualFollowing.length * weights.followingWeight;
    }

    // 3. Related Followers (15%) - Mutual followers
    if (currentUser.followers && targetUser.followers) {
        const mutualFollowers = currentUser.followers.filter(uid => targetUser.followers.includes(uid));
        score += mutualFollowers.length * weights.followersWeight;
    }

    // 4. Related Post Interactions (10%)
    // - Unique posts that both users have interacted with (liked, commented, bookmarked, bumped, or authored)
    let interactionCount = 0;
    if (currentUserInteractions && targetUserInteractions) {
        // Use pre-calculated sets for efficiency
        const smallerSet = currentUserInteractions.size < targetUserInteractions.size ? currentUserInteractions : targetUserInteractions;
        const largerSet = smallerSet === currentUserInteractions ? targetUserInteractions : currentUserInteractions;
        
        smallerSet.forEach(postId => {
            if (largerSet.has(postId)) interactionCount++;
        });
    } else if (posts) {
        // Fallback logic if sets aren't provided
        const cInteractions = new Set<number>();
        const tInteractions = new Set<number>();

        currentUser.likedPosts?.forEach(id => cInteractions.add(id));
        currentUser.bookmarkedPosts?.forEach(id => cInteractions.add(id));
        currentUser.bumpedPosts?.forEach(id => cInteractions.add(id));

        targetUser.likedPosts?.forEach(id => tInteractions.add(id));
        targetUser.bookmarkedPosts?.forEach(id => tInteractions.add(id));
        targetUser.bumpedPosts?.forEach(id => tInteractions.add(id));

        posts.forEach(post => {
            if (post?.author === currentUserId) cInteractions.add(post.id);
            if (post?.author === targetUserId) tInteractions.add(post.id);
            if (post?.comments?.some(c => c?.author === currentUserId)) cInteractions.add(post.id);
            if (post?.comments?.some(c => c?.author === targetUserId)) tInteractions.add(post.id);
        });

        cInteractions.forEach(id => {
            if (tInteractions.has(id)) interactionCount++;
        });
    }
    score += interactionCount * weights.postInteractionWeight;

    // 5. Same Communities (10%)
    if (communities) {
        const mutualCommunities = communities.filter(c => 
            c?.members && c.members.includes(currentUserId) && c.members.includes(targetUserId)
        );
        score += mutualCommunities.length * weights.communityWeight;
    }

    return score;
}

/**
 * Returns a sorted list of user IDs based on weighted interaction scores.
 * @param recentDmLimit Number of top recent DMs to pin at the start (default: 3)
 */
export function getWeightedUserList(
    currentUserId: string,
    allUserIds: string[] = [],
    usersData: Record<string, UserData> = {},
    conversations: Conversation[] = [],
    communities: Community[] = [],
    posts: Post[] = [],
    recentDmLimit: number = 3
): string[] {
    // 1. Get top recent DMs
    const recentConvoParticipants = (conversations || [])
        .filter(c => c && Array.isArray(c.participants) && c.participants.includes(currentUserId) && c.participants.length > 1)
        .sort((a, b) => {
            const aMsgs = a?.messages || [];
            const bMsgs = b?.messages || [];
            const aLastMsg = aMsgs.length > 0 ? aMsgs[aMsgs.length - 1] : null;
            const bLastMsg = bMsgs.length > 0 ? bMsgs[bMsgs.length - 1] : null;
            const aLast = aLastMsg && aLastMsg.timestamp ? new Date(aLastMsg.timestamp).getTime() : 0;
            const bLast = bLastMsg && bLastMsg.timestamp ? new Date(bLastMsg.timestamp).getTime() : 0;
            return bLast - aLast;
        })
        .flatMap(c => (c.participants || []).filter(uid => uid !== currentUserId));
    
    const topRecentDms = Array.from(new Set(recentConvoParticipants)).slice(0, recentDmLimit);

    // 2. Pre-calculate interaction sets for all relevant users to optimize scoring
    const userInteractionSets: Record<string, Set<number>> = {};
    
    // Initialize with data from UserData (likes, bookmarks, bumps)
    Object.keys(usersData || {}).forEach(uid => {
        const ud = usersData[uid];
        const set = new Set<number>();
        if (ud) {
            ud.likedPosts?.forEach(id => set.add(id));
            ud.bookmarkedPosts?.forEach(id => set.add(id));
            ud.bumpedPosts?.forEach(id => set.add(id));
        }
        userInteractionSets[uid] = set;
    });

    // Pass through posts once to handle authors and commenters
    if (posts) {
        posts.forEach(p => {
            if (p && p.author) {
                if (userInteractionSets[p.author]) userInteractionSets[p.author].add(p.id);
                p.comments?.forEach(c => {
                    if (c && c.author && userInteractionSets[c.author]) userInteractionSets[c.author].add(p.id);
                });
            }
        });
    }

    const currentUserInteractions = userInteractionSets[currentUserId] || new Set<number>();

    // 3. Calculate scores for all users not in top recent DMs
    const usersToScore = (allUserIds || []).filter(uid => uid && uid !== currentUserId && !topRecentDms.includes(uid));
    
    const scoredUsers = usersToScore.map(uid => ({
        uid,
        score: calculateInteractionScore(
            currentUserId, 
            uid, 
            usersData, 
            conversations, 
            communities, 
            posts, 
            DEFAULT_WEIGHTS,
            currentUserInteractions,
            userInteractionSets[uid]
        )
    }));

    // 4. Sort by score descending
    scoredUsers.sort((a, b) => b.score - a.score);

    // 5. Combine
    return [...topRecentDms, ...scoredUsers.map(u => u.uid)];
}
